import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Space } from 'antd';
import { Icons } from 'zf-web-components/icons';
import { AuthImage, Block, Spin } from '..';
import styles from './style.module.less';
import type { LazyImageProps } from './types';

export function LazyImage(props: LazyImageProps): React.ReactElement {
  const { lazy = true, ...imageProps } = props;
  const {
    src,
    quality,
    resizePercent,
    resize = 200,
    internal
  } = imageProps;

  const imgRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // 内网访问或显式关闭懒加载时，立即加载图片。
    if (internal === true || !lazy) {
      setVisible(true);
      return;
    }

    if (typeof window.IntersectionObserver !== 'undefined') {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.disconnect();
          }
        },
        {
          root: null, // 视口
          rootMargin: '0px',
          threshold: 0.1 // 可见比例达到 10% 就加载
        }
      );

      if (imgRef.current != null) {
        observer.observe(imgRef.current);
      }

      return (): void => {
        observer.disconnect();
      };
    }
    // 如果浏览器不支持 IntersectionObserver，直接显示图片
    setVisible(true);
  }, [internal, lazy]);

  const imageProcessUrl = useMemo(() => {
    if (internal === true) {
      return src;
    }

    const separator = (src ?? '').includes('?') ? '&' : '?';

    if (resizePercent != null) {
      return `${src}${separator}x-oss-process=image/resize,p_${resizePercent}`;
    }

    if (quality != null) {
      return `${src}${separator}x-oss-process=image/quality,q_${quality}`;
    }

    return `${src}${separator}x-oss-process=image/resize,w_${resize}`;
  }, [resize, resizePercent, quality, internal, src]);

  // 禁止右键菜单
  const handleContextMenu = (event: React.MouseEvent): void => {
    // 显示原生img是允许右键默认行为
    if (props.preview === false) {
      return;
    }
    event.preventDefault();
  };

  const onDownload = (): void => {
    props.download?.();
  };

  return (
    <div ref={imgRef} className={props.className} onContextMenu={handleContextMenu}>
      {!visible
        ? <div className={styles.spinWrapper}>
          <Spin />
        </div>
        : <>
          {/* 显示原生img */}
          <Block if={props.preview === false}>
            <img src={imageProcessUrl} alt='' className={styles.previewImage} />
          </Block>
          <Block if={props.preview !== false}>
            <AuthImage
              {...imageProps}
              src={imageProcessUrl}
              preview={{
                src,
                getContainer: false,
                toolbarRender: (
                  ignored,
                  {
                    transform: { scale },
                    actions: {
                      onFlipY,
                      onFlipX,
                      onRotateLeft,
                      onRotateRight,
                      onZoomOut,
                      onZoomIn
                    }
                  }
                ) => (
                  <Space size={12} className={styles.toolbarWrapper}>
                    <Block if={props.download != null}>
                      <Icons name='DownloadOutlined' onClick={onDownload} />
                    </Block>
                    <Icons name='SwapOutlined' rotate={90} onClick={onFlipY} />
                    <Icons name='SwapOutlined' onClick={onFlipX} />
                    <Icons name='RotateLeftOutlined' onClick={onRotateLeft} />
                    <Icons name='RotateRightOutlined' onClick={onRotateRight} />
                    <Icons name='ZoomOutOutlined' disabled={scale === 1} onClick={onZoomOut} />
                    <Icons name='ZoomInOutlined' disabled={scale === 50} onClick={onZoomIn} />
                  </Space>
                )
              }}
              fallback={src}
            />
          </Block>
        </>
      }
    </div>
  );
}
